import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';
import { heureVersMinutes } from '../utils/horaires';
import FilmModal from './FilmModal';
import '../styles/CinemasSection.css';

function extraireCineId(url) {
  const m = url.match(/[?&]cine=(\d+)/);
  return m ? m[1] : null;
}

function formatHeure(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function minutesFromISO(iso) {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function nomFilm(film) {
  const original = film.titreOriginal && film.titreOriginal !== film.titre
    ? ` (${film.titreOriginal})`
    : '';
  return `${film.titre}${original}`;
}

// "(2026, VF)" — année et version, quand disponibles; gère aussi les cas où
// l'une des deux informations manque, sans virgule ou parenthèses vides.
function meta(film, type) {
  const parts = [film.annee, type].filter(Boolean);
  return parts.length > 0 ? `(${parts.join(', ')})` : '';
}

function mapsUrl(nom) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${nom}, Paris`)}`;
}

// Distance brute plutôt qu'un temps de marche: valable quel que soit le
// mode de déplacement (à pied, vélo, transports...), contrairement à une
// estimation en minutes qui suppose la marche.
function libelleDistance(m) {
  if (m == null) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

function TitreFilm({ film, onFilmClick }) {
  if (!film.scUrl) {
    return <span className="seance-film">{nomFilm(film)}</span>;
  }
  return (
    <button className="seance-film seance-film-lien" onClick={() => onFilmClick(film)}>
      {nomFilm(film)}
    </button>
  );
}

// L'heure ouvre le lien de réservation quand il existe (sinon simple texte).
function HeureSeance({ s }) {
  if (!s.lien) {
    return <span className="seance-heure">{formatHeure(s.heure)}</span>;
  }
  return (
    <a href={s.lien} target="_blank" rel="noreferrer" className="seance-heure seance-heure-lien" title="Réserver">
      {formatHeure(s.heure)}
    </a>
  );
}

function texteSalle(s) {
  if (!s.salle) return null;
  return `Salle ${s.salle}`;
}

// Une seule ligne quand un seul des deux (film ou cinéma) doit être affiché
// (l'autre étant déjà l'en-tête du groupe, en mode "Par cinéma" ou "Par
// film"): tout tient alors sur la ligne 1 (titre/cinéma + distance + salle
// + version). La séance "Par horaire" affiche les deux (film ET cinéma) et
// garde donc 2 lignes, faute de place pour tout tenir sur une seule.
function SeanceLignes({ s, onFilmClick, showCinema, showFilm = true }) {
  const uneLigne = !showFilm || !showCinema;
  return (
    <div className="seance-card">
      <HeureSeance s={s} />
      <div className="seance-card-lignes">
        <div className="seance-card-ligne1">
          {showFilm ? (
            <>
              <TitreFilm film={s.film} onFilmClick={onFilmClick} />
              {meta(s.film, s.type) && <span className="seance-meta">{meta(s.film, s.type)}</span>}
              {uneLigne && texteSalle(s) && <span className="seance-salle">{texteSalle(s)}</span>}
            </>
          ) : (
            <>
              {showCinema && (
                <a href={mapsUrl(s.cinema.nom)} target="_blank" rel="noreferrer" className="seance-cinema-principal">
                  📍 {s.cinema.nom}
                </a>
              )}
              {s.cinema?.distance_m != null && (
                <span className="seance-meta">({libelleDistance(s.cinema.distance_m)})</span>
              )}
              {texteSalle(s) && <span className="seance-salle">{texteSalle(s)}</span>}
              {s.type && <span className="seance-meta">({s.type})</span>}
            </>
          )}
        </div>
        {!uneLigne && (
          <div className="seance-card-ligne2">
            {showCinema && (
              <a href={mapsUrl(s.cinema.nom)} target="_blank" rel="noreferrer" className="seance-cinema-lien">
                📍 {s.cinema.nom}
              </a>
            )}
            {s.cinema?.distance_m != null && (
              <span className="seance-distance">({libelleDistance(s.cinema.distance_m)})</span>
            )}
            {texteSalle(s) && <span className="seance-salle">{texteSalle(s)}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// Affiche les séances de tous les cinémas proches d'une piscine, pour le
// jour et à partir de l'heure choisis dans les contrôles de l'app. Les
// données viennent d'un cache régénéré une fois par jour côté serveur (pas
// d'appel au site source à chaque consultation).
function CinemasSection({ cinemas, day, desiredTime }) {
  const [sortMode, setSortMode] = useState('horaire'); // 'horaire' | 'cinema' | 'film'
  const [state, setState] = useState('loading');
  const [seances, setSeances] = useState([]);
  const [openFilm, setOpenFilm] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    fetch(`${API_BASE}/api/cinema/cache`)
      .then(r => r.json())
      .then(({ cache }) => {
        if (cancelled) return;
        const tout = [];
        for (const cinema of cinemas) {
          const cineId = extraireCineId(cinema.url);
          const seancesDuJour = cache?.[cineId]?.[day] || [];
          seancesDuJour.forEach(s => tout.push({ ...s, cinema }));
        }
        setSeances(tout);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => { cancelled = true; };
  }, [cinemas, day]);

  if (state === 'loading') return <p className="cinema-msg">Chargement des séances…</p>;
  if (state === 'error') return <p className="cinema-msg">Impossible de charger les séances</p>;

  const seuil = heureVersMinutes(desiredTime);
  const filtered = seances.filter(s => minutesFromISO(s.heure) >= seuil);

  if (filtered.length === 0) {
    return <p className="cinema-msg">Aucune séance après {desiredTime}</p>;
  }

  let contenu;
  if (sortMode === 'horaire') {
    const triees = [...filtered].sort((a, b) => minutesFromISO(a.heure) - minutesFromISO(b.heure));
    contenu = triees.map((s, i) => <SeanceLignes key={i} s={s} onFilmClick={setOpenFilm} showCinema />);
  } else if (sortMode === 'cinema') {
    const groupes = new Map();
    filtered.forEach(s => {
      if (!groupes.has(s.cinema.nom)) {
        groupes.set(s.cinema.nom, { cinema: s.cinema, items: [] });
      }
      groupes.get(s.cinema.nom).items.push(s);
    });
    const groupesTries = Array.from(groupes.values()).sort(
      (a, b) => a.cinema.distance_m - b.cinema.distance_m
    );
    groupesTries.forEach(g => g.items.sort((a, b) => minutesFromISO(a.heure) - minutesFromISO(b.heure)));

    contenu = groupesTries.map((g, gi) => (
      <div key={gi} className="cinema-groupe">
        <a href={mapsUrl(g.cinema.nom)} target="_blank" rel="noreferrer" className="cinema-groupe-titre">
          📍 {g.cinema.nom} ({libelleDistance(g.cinema.distance_m)})
        </a>
        {g.items.map((s, i) => (
          <SeanceLignes key={i} s={s} onFilmClick={setOpenFilm} showCinema={false} />
        ))}
      </div>
    ));
  } else {
    // 'film': un groupe par film, classés alphabétiquement (insensible aux
    // accents/casse), chaque groupe listant ses séances triées par heure
    // avec le cinéma affiché (plusieurs cinémas peuvent jouer le même film).
    const groupes = new Map();
    filtered.forEach(s => {
      const cle = s.film.id;
      if (!groupes.has(cle)) {
        groupes.set(cle, { film: s.film, items: [] });
      }
      groupes.get(cle).items.push(s);
    });
    const groupesTries = Array.from(groupes.values()).sort(
      (a, b) => nomFilm(a.film).localeCompare(nomFilm(b.film), 'fr', { sensitivity: 'base' })
    );
    groupesTries.forEach(g => g.items.sort((a, b) => minutesFromISO(a.heure) - minutesFromISO(b.heure)));

    contenu = groupesTries.map((g, gi) => (
      <div key={gi} className="cinema-groupe">
        <button className="cinema-groupe-titre cinema-groupe-titre-film" onClick={() => setOpenFilm(g.film)}>
          🎬 {nomFilm(g.film)}{g.film.annee && <span className="seance-meta"> ({g.film.annee})</span>}
        </button>
        {g.items.map((s, i) => (
          <SeanceLignes key={i} s={s} onFilmClick={setOpenFilm} showCinema showFilm={false} />
        ))}
      </div>
    ));
  }

  return (
    <div className="cinemas-merged">
      <h2 className="cinemas-count">{filtered.length} séance(s) trouvée(s)</h2>
      <div className="cinema-sort-toggle">
        <button
          className={sortMode === 'horaire' ? 'active' : ''}
          onClick={() => setSortMode('horaire')}
        >
          Par horaire
        </button>
        <button
          className={sortMode === 'cinema' ? 'active' : ''}
          onClick={() => setSortMode('cinema')}
        >
          Par cinéma
        </button>
        <button
          className={sortMode === 'film' ? 'active' : ''}
          onClick={() => setSortMode('film')}
        >
          Par film
        </button>
      </div>
      <div className="cinemas-list-scroll">
        {contenu}
      </div>

      <FilmModal film={openFilm} day={day} cinemas={cinemas} onClose={() => setOpenFilm(null)} />
    </div>
  );
}

export default CinemasSection;
